// A game mode pairs brain-exercise rules with presentation. Single-rule modes
// are practice tracks; a mode with `stages` is a curriculum ladder that walks
// through exercises as levels rise (the board clears and pace eases at each
// new exercise — see World's stage handling).

export interface StageDef {
  ruleId: string;
  /** The stage takes over at this level (stages sorted ascending). */
  fromLevel: number;
  /** Big friendly banner shown when the stage begins. */
  banner: string;
}

export interface GameMode {
  id: string;
  label: string;
  description: string;
  /** Rule for single-exercise modes; ignored when `stages` is present. */
  ruleId: string;
  themeId: string;
  stages?: readonly StageDef[];
}

export const GAME_MODES: Record<string, GameMode> = {
  journey: {
    id: 'journey',
    label: 'Journey',
    description: 'The full workout: match, make ten, then add and subtract — one gentle escalator.',
    ruleId: 'identity',
    themeId: 'day',
    stages: [
      { ruleId: 'identity', fromLevel: 1, banner: 'Match the number!' },
      { ruleId: 'complement', fromLevel: 4, banner: 'Make ten! 3 needs 7' },
      { ruleId: 'addition', fromLevel: 7, banner: 'Add them up!' },
      { ruleId: 'subtraction', fromLevel: 10, banner: 'Now take away!' },
      { ruleId: 'mixed', fromLevel: 13, banner: 'Everything, mixed!' },
    ],
  },
  classic: {
    id: 'classic',
    label: 'Number Match',
    description: 'Destroy each enemy by selecting its own number.',
    ruleId: 'identity',
    themeId: 'night',
  },
  makeTen: {
    id: 'makeTen',
    label: 'Make Ten',
    description: 'Hit each number with its complement to 10 (3↔7, 6↔4, 5↔5).',
    ruleId: 'complement',
    themeId: 'night',
  },
  addition: {
    id: 'addition',
    label: 'Add It Up',
    description: 'Each enemy is a sum like 3+4 — fire the answer.',
    ruleId: 'addition',
    themeId: 'night',
  },
  subtraction: {
    id: 'subtraction',
    label: 'Take Away',
    description: 'Each enemy is a difference like 8-3 — fire the answer.',
    ruleId: 'subtraction',
    themeId: 'night',
  },
};

/** The stage owning `level`, or null for single-rule modes. */
export function stageFor(mode: GameMode, level: number): StageDef | null {
  if (!mode.stages || mode.stages.length === 0) return null;
  let current = mode.stages[0];
  for (const stage of mode.stages) {
    if (stage.fromLevel <= level) current = stage;
    else break;
  }
  return current;
}

export const DEFAULT_MODE = GAME_MODES.journey;
